import { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Command } from 'cmdk'
import { useQuery } from '@tanstack/react-query'
import {
  Building2,
  Home as HomeIcon,
  Receipt,
  Search,
  Plus,
  Users,
  User,
  LayoutDashboard,
} from 'lucide-react'

import { useUiStore } from '@/stores/ui-store'
import {
  wspolnotyApi,
  lokaleApi,
  rozliczeniaApi,
} from '@/services/api'
import type { Lokal, Mieszkaniec } from '@/types'

const MIESIACE = [
  'styczeń',
  'luty',
  'marzec',
  'kwiecień',
  'maj',
  'czerwiec',
  'lipiec',
  'sierpień',
  'wrzesień',
  'październik',
  'listopad',
  'grudzień',
]

interface MieszkaniecRow {
  mieszkaniec: Mieszkaniec
  lokal: Lokal
}

export default function CommandPalette() {
  const navigate = useNavigate()
  const open = useUiStore((s) => s.commandPaletteOpen)
  const setOpen = useUiStore((s) => s.setCommandPaletteOpen)

  // Cache wyników między otwarciami palety. Włączane dopiero gdy paleta otwarta —
  // ale po pierwszym fetchu cache TanStack Query (5 min staleTime) pozostaje.
  const wspolnotyQuery = useQuery({
    queryKey: ['command-palette', 'wspolnoty'],
    queryFn: async () => (await wspolnotyApi.getAll()).data,
    enabled: open,
  })

  const lokaleQuery = useQuery({
    queryKey: ['command-palette', 'lokale'],
    queryFn: async () => (await lokaleApi.getAll()).data,
    enabled: open,
  })

  const rozliczeniaQuery = useQuery({
    queryKey: ['command-palette', 'rozliczenia'],
    queryFn: async () => (await rozliczeniaApi.getAll()).data,
    enabled: open,
  })

  // Mieszkańcy są pochodną lokali (nie ma osobnego /api/mieszkancy GET).
  const mieszkancy: MieszkaniecRow[] = useMemo(() => {
    const rows: MieszkaniecRow[] = []
    const lokale = lokaleQuery.data ?? []
    for (const lokal of lokale) {
      for (const m of lokal.mieszkancy ?? []) {
        rows.push({ mieszkaniec: m, lokal })
      }
    }
    return rows
  }, [lokaleQuery.data])

  // Mapowanie wspolnota_id -> nazwa (do etykiet rozliczeń/lokali).
  const wspolnotaNazwaById = useMemo(() => {
    const map = new Map<number, string>()
    for (const w of wspolnotyQuery.data ?? []) {
      map.set(w.id, w.nazwa)
    }
    return map
  }, [wspolnotyQuery.data])

  const handleSelect = (path: string) => {
    setOpen(false)
    navigate(path)
  }

  // Reset hash/scroll przy zamknięciu nie jest potrzebny — cmdk czyści input sam,
  // ale dodajemy własne czyszczenie value przy otwarciu, gdyby trzymał poprzednie.
  useEffect(() => {
    if (!open) return
    // brak akcji — cmdk samo zarządza fokusem inputu
  }, [open])

  const isLoading =
    wspolnotyQuery.isLoading ||
    lokaleQuery.isLoading ||
    rozliczeniaQuery.isLoading

  return (
    <Command.Dialog
      open={open}
      onOpenChange={setOpen}
      label="Globalne wyszukiwanie"
      // cmdk renderuje overlay + content. Stylujemy klasą.
      className="fixed inset-0 z-[100] flex items-start justify-center pt-[10vh] px-4"
      // Tło overlay przez wrapper (cmdk Dialog nie ma osobnego overlay slotu w prostym API).
    >
      {/* Overlay (klikalny — zamyka) */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={() => setOpen(false)}
        aria-hidden="true"
      />

      {/* Content */}
      <div className="relative w-full max-w-xl rounded-lg border border-gray-200 bg-white shadow-2xl overflow-hidden">
        <div className="flex items-center gap-2 border-b border-gray-200 px-3">
          <Search className="w-4 h-4 text-gray-400 flex-shrink-0" aria-hidden="true" />
          <Command.Input
            autoFocus
            placeholder="Szukaj wspólnot, lokali, mieszkańców, rozliczeń..."
            className="flex-1 h-12 bg-transparent outline-none text-sm placeholder:text-gray-400"
          />
          <kbd className="hidden sm:inline-flex items-center px-2 py-0.5 rounded border border-gray-200 text-[10px] text-gray-500 font-mono">
            ESC
          </kbd>
        </div>

        <Command.List className="max-h-[60vh] overflow-y-auto p-2">
          <Command.Empty className="py-6 text-center text-sm text-gray-500">
            {isLoading ? 'Ładowanie danych...' : 'Brak wyników'}
          </Command.Empty>

          {/* Szybkie akcje — zawsze widoczne */}
          <Command.Group
            heading="Akcje"
            className="text-xs font-medium text-gray-500 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5"
          >
            <PaletteItem
              icon={LayoutDashboard}
              onSelect={() => handleSelect('/dashboard')}
              label="Dashboard"
              hint="Przejdź do dashboardu"
              keywords={['dashboard', 'pulpit', 'glowna', 'strona główna']}
            />
            <PaletteItem
              icon={Plus}
              onSelect={() => handleSelect('/wspolnoty?add=1')}
              label="Dodaj wspólnotę"
              hint="Nowa wspólnota mieszkaniowa"
              keywords={['dodaj', 'nowa', 'wspolnota', 'wspólnota', 'create']}
            />
            <PaletteItem
              icon={Plus}
              onSelect={() => handleSelect('/lokale?add=1')}
              label="Dodaj lokal"
              hint="Nowy lokal mieszkalny"
              keywords={['dodaj', 'nowy', 'lokal', 'mieszkanie', 'create']}
            />
            <PaletteItem
              icon={Plus}
              onSelect={() => handleSelect('/rozliczenia?add=1')}
              label="Nowe rozliczenie"
              hint="Wygeneruj rozliczenie miesięczne"
              keywords={['dodaj', 'nowe', 'rozliczenie', 'create', 'miesiac']}
            />
          </Command.Group>

          {/* Wspólnoty */}
          {(wspolnotyQuery.data?.length ?? 0) > 0 && (
            <Command.Group
              heading="Wspólnoty"
              className="text-xs font-medium text-gray-500 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:mt-2"
            >
              {wspolnotyQuery.data!.map((w) => (
                <PaletteItem
                  key={`w-${w.id}`}
                  icon={Building2}
                  onSelect={() => handleSelect(`/wspolnoty/${w.id}`)}
                  label={w.nazwa}
                  hint={w.adres}
                  keywords={[w.nazwa, w.adres, w.miasto, w.kod_pocztowy].filter(Boolean) as string[]}
                />
              ))}
            </Command.Group>
          )}

          {/* Lokale */}
          {(lokaleQuery.data?.length ?? 0) > 0 && (
            <Command.Group
              heading="Lokale"
              className="text-xs font-medium text-gray-500 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:mt-2"
            >
              {lokaleQuery.data!.map((l) => {
                const wspolnotaNazwa =
                  l.wspolnota?.nazwa ?? wspolnotaNazwaById.get(l.wspolnota_id) ?? 'Wspólnota'
                return (
                  <PaletteItem
                    key={`l-${l.id}`}
                    icon={HomeIcon}
                    onSelect={() => handleSelect(`/lokale/${l.id}`)}
                    label={`Lokal ${l.numer}`}
                    hint={wspolnotaNazwa}
                    keywords={[l.numer, wspolnotaNazwa, `lokal ${l.numer}`]}
                  />
                )
              })}
            </Command.Group>
          )}

          {/* Mieszkańcy (z lokali) */}
          {mieszkancy.length > 0 && (
            <Command.Group
              heading="Mieszkańcy"
              className="text-xs font-medium text-gray-500 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:mt-2"
            >
              {mieszkancy.map(({ mieszkaniec: m, lokal }) => {
                const wspolnotaNazwa =
                  lokal.wspolnota?.nazwa ?? wspolnotaNazwaById.get(lokal.wspolnota_id) ?? ''
                const fullName = `${m.imie} ${m.nazwisko}`.trim()
                const lokalLabel = `Lokal ${lokal.numer}${wspolnotaNazwa ? ` · ${wspolnotaNazwa}` : ''}`
                return (
                  <PaletteItem
                    key={`m-${m.id}-${lokal.id}`}
                    icon={m.glowny ? Users : User}
                    onSelect={() => handleSelect(`/lokale/${lokal.id}`)}
                    label={fullName || `Mieszkaniec #${m.id}`}
                    hint={lokalLabel}
                    keywords={[m.imie, m.nazwisko, fullName, lokal.numer, wspolnotaNazwa, m.email ?? '']}
                  />
                )
              })}
            </Command.Group>
          )}

          {/* Rozliczenia */}
          {(rozliczeniaQuery.data?.length ?? 0) > 0 && (
            <Command.Group
              heading="Rozliczenia"
              className="text-xs font-medium text-gray-500 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:mt-2"
            >
              {rozliczeniaQuery.data!.map((r) => {
                const miesiacNazwa = MIESIACE[r.miesiac - 1] ?? `${r.miesiac}`
                const okres = `${miesiacNazwa} ${r.rok}`
                const label = `${r.wspolnota_nazwa} — ${okres}`
                return (
                  <PaletteItem
                    key={`r-${r.id}`}
                    icon={Receipt}
                    onSelect={() => handleSelect(`/rozliczenia/${r.id}`)}
                    label={label}
                    hint={`Status: ${r.status}`}
                    keywords={[
                      r.wspolnota_nazwa,
                      okres,
                      miesiacNazwa,
                      String(r.rok),
                      String(r.miesiac),
                      r.status,
                    ]}
                  />
                )
              })}
            </Command.Group>
          )}
        </Command.List>

        <div className="border-t border-gray-200 px-3 py-2 text-[11px] text-gray-500 flex items-center justify-between bg-gray-50">
          <div className="flex items-center gap-3">
            <span>
              <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded font-mono text-[10px]">
                ↑↓
              </kbd>{' '}
              nawigacja
            </span>
            <span>
              <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded font-mono text-[10px]">
                ↵
              </kbd>{' '}
              wybierz
            </span>
            <span>
              <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded font-mono text-[10px]">
                ESC
              </kbd>{' '}
              zamknij
            </span>
          </div>
        </div>
      </div>
    </Command.Dialog>
  )
}

interface PaletteItemProps {
  icon: React.ElementType
  label: string
  hint?: string
  keywords?: string[]
  onSelect: () => void
}

function PaletteItem({ icon: Icon, label, hint, keywords, onSelect }: PaletteItemProps) {
  // cmdk używa tekstu z `value` lub `keywords` do dopasowania (fuzzy search wbudowany).
  // Składamy value jako label + hint + keywords, by szukanie obejmowało wszystkie pola.
  const value = [label, hint, ...(keywords ?? [])].filter(Boolean).join(' ')
  return (
    <Command.Item
      value={value}
      onSelect={onSelect}
      className="flex items-center gap-3 px-2 py-2 rounded-md cursor-pointer text-sm
        text-gray-700 aria-selected:bg-primary/10 aria-selected:text-primary
        data-[selected=true]:bg-primary/10 data-[selected=true]:text-primary"
    >
      <Icon className="w-4 h-4 flex-shrink-0 text-gray-400 group-aria-selected:text-primary" aria-hidden="true" />
      <div className="flex flex-col min-w-0 flex-1">
        <span className="truncate">{label}</span>
        {hint && <span className="text-xs text-gray-500 truncate">{hint}</span>}
      </div>
    </Command.Item>
  )
}
